import { FC, useEffect } from 'react';
import { Box, Button, Divider, styled, Typography, useTheme } from '@mui/material';
import { useAppSelector } from '@/store/hooks';
import { selectFrequencyAnswers } from '@/store/reducers/survey/surveySlice';
import RewardsImage from '@/assets/images/rewards.png';
import useSurveyNavigate from '@/hooks/useSurveyNavigate';
import analytics from '@/utils/analytics/analyticsInstance';

const Container = styled(Box)(() => ({
  flex: 1,
  display: 'flex',
  flexDirection: 'column',
}));

const Main = styled(Box)(() => ({
  flex: 1,
  gap: 12,
  padding: 16,
  maxWidth: 600,
  display: 'flex',
  flexDirection: 'column',
  justifyContent: 'center',
  alignItems: 'center',
  alignSelf: 'center',
}));

const Image = styled('img')(() => ({
  position: 'absolute',
  width: 80,
  maxWidth: 80,
  bottom: -38,
  right: 16,
}));

const CustomDivider = styled(Divider)(({ theme }) => ({
  backgroundColor: theme.palette.border.main,
  height: 1,
  borderBottomWidth: 0,
}));

const RewardBorder = styled(Box)(() => ({
  padding: 4,
  maxWidth: 288,
  maxHeight: 180,
  borderRadius: 24,
  width: '100%',
  height: '100%',
  position: 'relative',
  background: 'linear-gradient(to bottom, #CCFF00, #66CC00)',
}));

const RewardWrapper = styled(Box)(({ theme }) => ({
  width: '100%',
  height: '100%',
  display: 'grid',
  placeItems: 'center',
  borderRadius: 20,
  backgroundColor: theme.palette.background.default,
}));

const Footer = styled('footer')(() => ({
  gap: 16,
  paddingBottom: 16,
  display: 'flex',
  flexDirection: 'column',
}));

const mappedRewards: Record<string, string> = {
  'less than $100': '$75.4',
  '$100-200': '$105.8',
  '$200-400': '$114.3',
  'more than $400': '$142.5',
};

const PotentialRewardPage: FC = () => {
  const theme = useTheme();
  const { next } = useSurveyNavigate();
  const rewards = useAppSelector(selectFrequencyAnswers);
  const potentialReward = mappedRewards[rewards[1].answer as string];

  useEffect(() => {
    analytics.track('webOnboardingScreenOpened', {
      screen_title: 'Potential reward',
    });
  }, []);

  return (
    <Container>
      <Main>
        <Typography
          fontSize={28}
          fontWeight={700}
          lineHeight="32px"
          color="black"
          textAlign="center"
        >
          Your potential reward:
        </Typography>
        <RewardBorder>
          <RewardWrapper>
            <Typography fontSize={52} fontWeight={700}>
              {potentialReward}
            </Typography>
          </RewardWrapper>
          <Image src={RewardsImage} />
        </RewardBorder>
      </Main>
      <Footer>
        <Typography
          variant="caption"
          color={theme.palette.text.disabled}
          fontWeight={400}
          textAlign="center"
          lineHeight="16px"
          whiteSpace="pre-line"
        >
          {'Annual estimate based on your \ninput and average reward rates.'}
        </Typography>
        <CustomDivider />
        <Box paddingInline={2} flex={1} sx={{ display: 'flex', justifyContent: 'center' }}>
          <Button
            onClick={next}
            variant="contained"
            size="large"
            sx={{ maxWidth: 600, width: '100%' }}
          >
            Continue
          </Button>
        </Box>
      </Footer>
    </Container>
  );
};

export default PotentialRewardPage;
